// HowItCountsDialog: bottone "Come si conta" + dialog con le spiegazioni
// dei calcoli. Riusa le chiavi i18n del namespace `players` (le stesse
// usate dal dialog del Confronto coach), così l'utente vede le stesse
// definizioni ovunque siano applicate.
//
// Due varianti:
//   - "full"        → dato/cosa incluso, esclusioni, filtri, rating
//                     top-10, shrinkage, score role-aware. Usato nei
//                     contesti dove c'è uno score 0-100 (PlayerRoleComparison).
//   - "leaderboard" → dato/cosa incluso, esclusioni, limite EA, rating
//                     top-10. Niente shrinkage/score perché in classifica
//                     i contatori sono cumulativi puri.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Sparkles, Trophy } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";

export function HowItCountsDialog({
  variant = "full",
}: {
  variant?: "full" | "leaderboard";
}) {
  const { t } = useTranslation("players");
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-full border bg-card hover:bg-muted/60 px-2.5 py-1 text-xs font-medium text-foreground/80 transition-colors"
      >
        <Info className="h-3.5 w-3.5" />
        {t("compare.info.cta")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("compare.info.title")}</DialogTitle>
            <DialogDescription>{t("compare.info.intro")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <section className="space-y-1.5">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                {t("compare.info.included.title")}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t("compare.info.included.body")}
              </p>
            </section>
            <section className="space-y-1.5">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                {t("compare.info.excluded.title")}
              </h3>
              <ul className="text-muted-foreground leading-relaxed list-disc pl-5 space-y-1">
                <li>{t("compare.info.excluded.manual")}</li>
                <li>{t("compare.info.excluded.career")}</li>
              </ul>
            </section>
            {variant === "full" && (
              <section className="space-y-1.5">
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                  {t("compare.info.filters.title")}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t("compare.info.filters.body")}
                </p>
              </section>
            )}
            <section className="space-y-1.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
              <h3 className="text-xs uppercase tracking-wide text-primary font-semibold flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                {t("compare.info.rating.title")}
              </h3>
              <p className="text-foreground/90 leading-relaxed">
                {t("compare.info.rating.body")}
              </p>
              <p className="text-muted-foreground leading-relaxed text-xs">
                {t("compare.info.rating.example")}
              </p>
            </section>
            {variant === "full" && (
              <>
                <section className="space-y-1.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
                  <h3 className="text-xs uppercase tracking-wide text-primary font-semibold flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    {t("compare.info.shrinkage.title")}
                  </h3>
                  <p className="text-foreground/90 leading-relaxed">
                    {t("compare.info.shrinkage.body")}
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-xs">
                    {t("compare.info.shrinkage.example")}
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-xs italic">
                    <span className="text-primary font-semibold">*</span> {t("compare.info.shrinkage.markLegend")}
                  </p>
                </section>
                <section className="space-y-1.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
                  <h3 className="text-xs uppercase tracking-wide text-primary font-semibold flex items-center gap-1.5">
                    <Trophy className="h-3.5 w-3.5" />
                    {t("compare.info.score.title")}
                  </h3>
                  <p className="text-foreground/90 leading-relaxed">
                    {t("compare.info.score.body")}
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-xs">
                    {t("compare.info.score.confidence")}
                  </p>
                </section>
              </>
            )}
            <section className="space-y-1.5">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                {t("compare.info.limits.title")}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t("compare.info.limits.body")}
              </p>
            </section>
            <div className="pt-2">
              <Button type="button" className="w-full" onClick={() => setOpen(false)}>
                {t("compare.info.gotIt")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
